<template>
    <div class="grid">
        <Toast/>

        <!-- ============ Cabecera ============ -->
        <div class="col-12">
            <div class="card" style="margin-bottom: 0;">
                <div style="display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:0.5rem;">
                    <h4 style="margin:0;"><i class="pi pi-wallet" style="margin-right:0.5rem;"></i>Cuentas por Cobrar / Pagar</h4>
                    <Button label="Actualizar" icon="pi pi-refresh" class="p-button-outlined p-button-sm" :loading="loading" @click="load"/>
                </div>
                <small style="color: var(--text-color-secondary);">Saldos vivos al día de hoy.</small>
            </div>
        </div>

        <!-- Por Cobrar -->
        <div class="col-12 md:col-6 lg:col-4">
            <div class="card kpi-panel kpi-receivable">
                <div class="kpi-title"><i class="pi pi-arrow-down-left"></i> Por cobrar</div>
                <div class="kpi-total">${{ money(receivable.total) }}</div>
                <div class="kpi-count">{{ receivable.count }} documento{{ receivable.count === 1 ? '' : 's' }}</div>
                <div class="kpi-breakdown">
                    <div class="kpi-chip">
                        <span class="kpi-chip-label">Vigente</span>
                        <span class="kpi-chip-value ok">${{ money(receivable.current) }}</span>
                    </div>
                    <div class="kpi-chip">
                        <span class="kpi-chip-label">Vencido</span>
                        <span class="kpi-chip-value bad">${{ money(receivable.overdue) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Por Pagar -->
        <div class="col-12 md:col-6 lg:col-4">
            <div class="card kpi-panel kpi-payable">
                <div class="kpi-title"><i class="pi pi-arrow-up-right"></i> Por pagar</div>
                <div class="kpi-total">${{ money(payable.total) }}</div>
                <div class="kpi-count">{{ payable.count }} documento{{ payable.count === 1 ? '' : 's' }}</div>
                <div class="kpi-breakdown">
                    <div class="kpi-chip">
                        <span class="kpi-chip-label">Vigente</span>
                        <span class="kpi-chip-value ok">${{ money(payable.current) }}</span>
                    </div>
                    <div class="kpi-chip">
                        <span class="kpi-chip-label">Vencido</span>
                        <span class="kpi-chip-value bad">${{ money(payable.overdue) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Neto -->
        <div class="col-12 lg:col-4">
            <div class="card kpi-panel kpi-net">
                <div class="kpi-title"><i class="pi pi-chart-line"></i> Posición neta</div>
                <div class="kpi-total" :class="netClass">${{ money(data.net) }}</div>
                <div class="kpi-count">Por cobrar − por pagar</div>
                <div class="kpi-net-hint">
                    {{ Number(data.net) >= 0 ? 'A tu favor' : 'En contra' }}
                </div>
            </div>
        </div>

        <!-- ============ Tabla ============ -->
        <div class="col-12">
            <div class="card">
                <div class="table-toolbar">
                    <SelectButton v-model="activeSide" :options="sideOptions" optionLabel="label" optionValue="value"
                                  :allowEmpty="false" aria-labelledby="toggle-cartera"/>
                    <div class="table-filters">
                        <Button :label="isReceivable ? 'Registrar cobros' : 'Registrar pagos'" icon="pi pi-money-bill"
                                class="p-button-success" @click="openBulk"/>
                        <SelectButton v-model="stateFilter" :options="stateOptions" optionLabel="label" optionValue="value" :allowEmpty="false"/>
                        <span class="p-input-icon-left">
                            <i class="pi pi-search"/>
                            <InputText v-model="search" :placeholder="isReceivable ? 'Buscar cliente o Nº...' : 'Buscar proveedor o Nº...'"/>
                        </span>
                    </div>
                </div>

                <DataTable :value="filteredDocuments" :loading="loading" responsiveLayout="scroll"
                           :paginator="filteredDocuments.length > 12" :rows="12" dataKey="id"
                           class="p-datatable-sm">
                    <template #empty>
                        <div style="text-align:center; padding:1.5rem; color: var(--text-color-secondary);">
                            <i class="pi pi-check-circle" style="font-size:1.5rem; display:block; margin-bottom:0.5rem; color:#22c55e;"></i>
                            No hay {{ isReceivable ? 'cuentas por cobrar' : 'cuentas por pagar' }} con los filtros actuales.
                        </div>
                    </template>

                    <Column field="number" :header="isReceivable ? 'Factura' : 'Documento'" style="min-width:9rem;">
                        <!-- La compra por la factura del proveedor; su retención, debajo (H69). -->
                        <template #body="{ data: row }">
                            <span style="font-weight:600;">{{ row.number }}</span>
                            <small v-if="row.retentionNumber" style="display:block; color:#777;">{{ row.retentionNumber }}</small>
                        </template>
                    </Column>

                    <Column field="party" :header="isReceivable ? 'Cliente' : 'Proveedor'" style="min-width:12rem;"></Column>

                    <Column header="Emitido" style="min-width:8rem;">
                        <template #body="{ data: row }">{{ formatDate(row.date) }}</template>
                    </Column>

                    <Column header="Vence" style="min-width:8rem;">
                        <template #body="{ data: row }">
                            <span v-if="row.dueDate">{{ formatDate(row.dueDate) }}</span>
                            <span v-else style="color: var(--text-color-secondary);">—</span>
                        </template>
                    </Column>

                    <Column header="Estado" style="min-width:7rem;">
                        <template #body="{ data: row }">
                            <Tag :value="row.state === 'VENCIDO' ? 'Vencido' : 'Vigente'"
                                 :severity="row.state === 'VENCIDO' ? 'danger' : 'success'"/>
                        </template>
                    </Column>

                    <Column header="Total" style="min-width:7rem;" bodyStyle="text-align:right;" headerStyle="text-align:right; justify-content:flex-end;">
                        <template #body="{ data: row }">${{ money(row.total) }}</template>
                    </Column>

                    <!-- Explica el hueco entre Total (bruto) y Pendiente (neto) cuando
                         hay retención; sin esta columna se lee como si el monto retenido
                         ya se hubiera abonado. Aplica a los DOS lados: en compras tú
                         retienes al proveedor, en ventas te retiene el cliente. -->
                    <Column v-if="hasRetainedRows" header="Retenido" style="min-width:8rem;" bodyStyle="text-align:right;" headerStyle="text-align:right; justify-content:flex-end;">
                        <template #body="{ data: row }">
                            <span v-if="Number(row.retained) > 0" style="color: #dc3545;"
                                  v-tooltip.top="isReceivable ? 'Te lo retuvo el cliente: no lo vas a cobrar, es crédito fiscal con el SRI' : 'Retención al proveedor: no sale de caja, se declara al SRI'">
                                − ${{ money(row.retained) }}
                            </span>
                            <span v-else style="color: var(--text-color-secondary);">—</span>
                        </template>
                    </Column>

                    <Column header="Abonado" style="min-width:8rem;" bodyStyle="text-align:right;" headerStyle="text-align:right; justify-content:flex-end;">
                        <template #body="{ data: row }">
                            <span v-if="Number(row.paid) > 0">
                                <a class="abono-link" @click="openHistory(row)">
                                    ${{ money(row.paid) }}
                                    <Badge :value="row.payments.length" severity="info"/>
                                </a>
                            </span>
                            <span v-else style="color: var(--text-color-secondary);">—</span>
                        </template>
                    </Column>

                    <Column header="Pendiente" style="min-width:8rem;" bodyStyle="text-align:right;" headerStyle="text-align:right; justify-content:flex-end;">
                        <template #body="{ data: row }">
                            <span :style="{ fontWeight:700, color: row.state === 'VENCIDO' ? '#dc3545' : '#111' }">${{ money(row.pending) }}</span>
                        </template>
                    </Column>

                    <!-- Los dos botones van en una sola línea: si se dejan como
                         hermanos sueltos, al angostar la columna el de retención
                         cae debajo y se monta sobre el de cobro. -->
                    <Column header="" :style="isReceivable ? 'min-width:13rem;' : 'min-width:9rem;'" bodyStyle="text-align:right;">
                        <template #body="{ data: row }">
                            <div class="row-actions">
                                <Button :label="isReceivable ? 'Cobrar' : 'Pagar'" icon="pi pi-wallet"
                                        class="p-button-sm p-button-success" @click="openPayment(row)"/>
                                <Button v-if="isReceivable" icon="pi pi-percentage"
                                        class="p-button-sm p-button-outlined"
                                        v-tooltip.top="'Registrar retención que te practicó el cliente'"
                                        @click="openRetention(row)"/>
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <!-- Diálogo de abono / cobro-pago (reutiliza AccountEntry en modo 'partial') -->
        <AccountEntry v-if="selectedDoc" :data="selectedDoc" :isVisible="accountEntryDialog" mode="partial"
                      :transactionType="isReceivable ? 'sale' : 'purchase'"
                      @onSave="onSavePayment" @onCancel="onCancelPayment"/>

        <!-- Retención recibida: manual o desde el XML del comprobante -->
        <Dialog v-model:visible="retentionDialog" header="Retención recibida" :modal="true"
                :style="{width: '480px'}">
            <p style="color:#555; margin-top:0;">
                Cuando un cliente te retiene, esa parte <b>no te la va a pagar</b>: es un crédito
                fiscal con el SRI. Al registrarla, la factura deja de aparecer como deuda pendiente.
            </p>
            <p v-if="retentionDoc" style="color:#777; font-size:0.9rem;">
                {{ retentionDoc.number }} · Total ${{ money(retentionDoc.total) }} ·
                Pendiente ${{ money(retentionDoc.pending) }}
            </p>

            <div style="border:1px dashed #ccc; border-radius:6px; padding:0.75rem; margin-bottom:1rem;">
                <label style="display:block; font-size:0.85rem; color:#777; margin-bottom:0.4rem;">
                    ¿Tienes el XML del comprobante? Súbelo y se llena solo.
                </label>
                <FileUpload mode="basic" accept=".xml" :auto="true" :customUpload="true"
                            chooseLabel="Subir XML" @uploader="onRetentionXml"/>
            </div>

            <div style="display:flex; gap:0.75rem;">
                <div style="flex:1;">
                    <label style="display:block; font-size:0.85rem; color:#777;">Retención de IVA</label>
                    <InputNumber v-model="retention.ivaAmount" mode="decimal" :minFractionDigits="2"
                                 :maxFractionDigits="2" style="width:100%;"/>
                </div>
                <div style="flex:1;">
                    <label style="display:block; font-size:0.85rem; color:#777;">Retención de renta</label>
                    <InputNumber v-model="retention.rentAmount" mode="decimal" :minFractionDigits="2"
                                 :maxFractionDigits="2" style="width:100%;"/>
                </div>
            </div>
            <div class="mt-3">
                <label style="display:block; font-size:0.85rem; color:#777;">Nº de comprobante</label>
                <InputText v-model="retention.receipt" placeholder="001-002-000000456" style="width:100%;"/>
            </div>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="retentionDialog = false"/>
                <Button label="Registrar" icon="pi pi-check" :loading="savingRetention"
                        @click="saveRetention"/>
            </template>
        </Dialog>

        <!-- Historial de abonos (read-only) -->
        <PaymentHistoryDialog :payments="historyPayments" :isVisible="paymentHistoryDialog" @onClose="paymentHistoryDialog = false"/>

        <!-- Cobro/pago en lote (una o más ventas/compras de una contraparte) -->
        <BulkPaymentDialog :isVisible="bulkDialog" :type="activeSide" :documents="sideData.documents"
                           @onSave="onBulkSave" @onCancel="onBulkCancel"/>
    </div>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import { isAlreadyCollected, alreadyCollectedToast } from './accountEntryRules';
import AccountEntry from './AccountEntry.vue';
import PaymentHistoryDialog from './PaymentHistoryDialog.vue';
import BulkPaymentDialog from './BulkPaymentDialog.vue';

const emptySide = { total: '0.00', current: '0.00', overdue: '0.00', count: 0, documents: [] };

export default {
    components: { AccountEntry, PaymentHistoryDialog, BulkPaymentDialog },
    data() {
        return {
            loading: false,
            data: { receivable: { ...emptySide }, payable: { ...emptySide }, net: '0.00' },
            activeSide: 'receivable',      // 'receivable' | 'payable'
            search: '',
            stateFilter: 'ALL',            // 'ALL' | 'VIGENTE' | 'VENCIDO'
            selectedDoc: null,             // fila transformada para AccountEntry
            accountEntryDialog: false,
            paymentHistoryDialog: false,
            historyPayments: [],
            bulkDialog: false,
            retentionDialog: false,
            retentionDoc: null,
            savingRetention: false,
            retention: { ivaAmount: null, rentAmount: null, receipt: '' },
            sideOptions: [
                { label: 'Por Cobrar', value: 'receivable' },
                { label: 'Por Pagar', value: 'payable' },
            ],
            stateOptions: [
                { label: 'Todos', value: 'ALL' },
                { label: 'Vigentes', value: 'VIGENTE' },
                { label: 'Vencidos', value: 'VENCIDO' },
            ],
        };
    },
    computed: {
        isReceivable() {
            return this.activeSide === 'receivable';
        },
        receivable() {
            return this.data.receivable || emptySide;
        },
        payable() {
            return this.data.payable || emptySide;
        },
        sideData() {
            return this.isReceivable ? this.receivable : this.payable;
        },
        netClass() {
            return Number(this.data.net) >= 0 ? 'ok' : 'bad';
        },
        hasRetainedRows() {
            // La columna "Retenido" solo aparece si algún documento de la cartera
            // trae retención: en negocios que no retienen sería una columna de
            // guiones. Se evalúa sobre el lado completo, no sobre
            // `filteredDocuments`, para que la tabla no gane/pierda una columna
            // al escribir en el buscador.
            return (this.sideData.documents || []).some(d => Number(d.retained) > 0);
        },
        filteredDocuments() {
            let docs = this.sideData.documents || [];
            if (this.stateFilter !== 'ALL') {
                docs = docs.filter(d => d.state === this.stateFilter);
            }
            const q = this.search.trim().toLowerCase();
            if (q) {
                docs = docs.filter(d =>
                    (d.party || '').toLowerCase().includes(q) ||
                    (d.number || '').toLowerCase().includes(q) ||
                    (d.retentionNumber || '').toLowerCase().includes(q)
                );
            }
            return docs;
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        openRetention(row) {
            this.retentionDoc = row;
            this.retention = { ivaAmount: null, rentAmount: null, receipt: '' };
            this.retentionDialog = true;
        },
        async onRetentionXml(event) {
            // El comprobante ya trae el desglose IVA/renta y su numero: subirlo evita
            // teclearlo y deja el dato exacto para el ATS.
            const file = event.files && event.files[0];
            if (!file || !this.retentionDoc) return;
            this.savingRetention = true;
            const response = await httpRequest.uploadSaleRetentionXml(file, this.retentionDoc.id);
            this.savingRetention = false;
            if (response.status === 200) {
                this.retentionDialog = false;
                this.$toast.add({severity: 'success', summary: 'Retención registrada',
                                 detail: 'Se leyó del comprobante', life: 4000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo leer el XML',
                                 detail: response.error || '', life: 6000});
            }
        },
        async saveRetention() {
            if (!this.retention.ivaAmount && !this.retention.rentAmount) {
                this.$toast.add({severity: 'warn', summary: 'Faltan datos',
                                 detail: 'Indica cuánto te retuvieron', life: 4000});
                return;
            }
            this.savingRetention = true;
            const response = await httpRequest.registerSaleRetention({
                saleId: this.retentionDoc.id,
                ivaAmount: String(this.retention.ivaAmount || 0),
                rentAmount: String(this.retention.rentAmount || 0),
                receipt: this.retention.receipt,
            });
            this.savingRetention = false;
            if (response.status === 200) {
                this.retentionDialog = false;
                this.$toast.add({severity: 'success', summary: 'Retención registrada', life: 4000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo registrar',
                                 detail: response.error || '', life: 6000});
            }
        },
        async load() {
            this.loading = true;
            const response = await httpRequest.getReceivablesPayablesDetail();
            this.loading = false;
            if (response && response.status === 200 && response.data) {
                this.data = response.data;
            } else {
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la cartera. Intente nuevamente.', life: 4000 });
            }
        },
        money(value) {
            const n = Number(value || 0);
            return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(raw) {
            if (!raw) return '';
            const date = new Date(raw.toString().replace(' ', 'T'));
            if (isNaN(date.getTime())) return raw.toString();
            return date.toLocaleDateString('es-EC', { year: 'numeric', month: 'short', day: 'numeric' });
        },
        openHistory(row) {
            this.historyPayments = row.payments || [];
            this.paymentHistoryDialog = true;
        },
        openPayment(row) {
            // Construye el `data` que AccountEntry espera en modo abono. El saldo
            // pendiente y los abonos previos vienen del backend; abonar el saldo
            // completo marca el documento como cobrado/pagado.
            const base = {
                id: row.id,
                total: row.total,
                paidAmount: row.paid,
                // `retainedAmount` explica el hueco Total − Pendiente en compras
                // retenidas; AccountEntry lo muestra en modo abono.
                retainedAmount: row.retained,
                pendingAmount: row.pending,
                payments: row.payments || [],
                edoc: null,
                hasRetention: false,
            };
            if (this.isReceivable) {
                base.customer = { name: row.party };
            } else {
                base.vendorName = row.party;
                base.reason = null;
            }
            this.selectedDoc = base;
            this.accountEntryDialog = true;
        },
        onCancelPayment() {
            this.accountEntryDialog = false;
        },
        async onSavePayment(payload) {
            this.accountEntryDialog = false;
            if (!payload || payload.partial !== true || !this.selectedDoc) return;

            const receivable = this.isReceivable;
            const response = await httpRequest.registerPayment({
                saleId: receivable ? this.selectedDoc.id : null,
                purchaseId: receivable ? null : this.selectedDoc.id,
                amount: payload.amount,
                accountId: payload.accountId,
                paymentMethod: payload.paymentMethod,
                approvalNumber: payload.approvalNumber,
                proof: payload.proof,
            });

            if (response && response.status === 200) {
                if (payload.completes) {
                    this.$toast.add({
                        severity: 'success',
                        summary: receivable ? 'Venta cobrada' : 'Compra pagada',
                        detail: `El abono completó el saldo y el documento quedó ${receivable ? 'cobrado' : 'pagado'}.`,
                        life: 4000,
                    });
                } else {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Abono registrado',
                        detail: `Abono de $${this.money(payload.amount)} registrado correctamente.`,
                        life: 4000,
                    });
                }
                await this.load();
            } else if (isAlreadyCollected(response)) {
                // Ya estaba registrado (910): se avisa y se recarga la lista, sin invitar a reintentar.
                this.$toast.add(alreadyCollectedToast(response, receivable ? 'Esta venta ya fue cobrada' : 'Esta compra ya fue pagada'));
                await this.load();
            } else {
                this.$toast.add({
                    severity: 'error',
                    summary: 'No se pudo registrar',
                    detail: (response && response.error) ? response.error : 'Intente nuevamente.',
                    life: 5000,
                });
            }
        },
        openBulk() {
            this.bulkDialog = true;
        },
        onBulkCancel() {
            this.bulkDialog = false;
        },
        async onBulkSave(payload) {
            this.bulkDialog = false;
            if (!payload || !payload.allocations || payload.allocations.length === 0) return;

            const response = await httpRequest.registerBulkPayment({
                type: this.activeSide,
                allocations: payload.allocations,
                accountId: payload.accountId,
                paymentMethod: payload.paymentMethod,
                approvalNumber: payload.approvalNumber,
                proof: payload.proof,
            });

            if (response && response.status === 200) {
                const d = response.data || {};
                const parts = [];
                if (d.paidCount) parts.push(`${d.paidCount} saldado${d.paidCount === 1 ? '' : 's'}`);
                if (d.partialCount) parts.push(`${d.partialCount} abonado${d.partialCount === 1 ? '' : 's'}`);
                this.$toast.add({
                    severity: 'success',
                    summary: this.isReceivable ? 'Cobro registrado' : 'Pago registrado',
                    detail: `$${this.money(d.totalApplied)} aplicado a ${d.affected} documento${d.affected === 1 ? '' : 's'}`
                        + (parts.length ? ` (${parts.join(', ')}).` : '.'),
                    life: 5000,
                });
                await this.load();
            } else {
                this.$toast.add({
                    severity: 'error',
                    summary: 'No se pudo registrar',
                    detail: (response && response.error) ? response.error : 'Intente nuevamente.',
                    life: 5000,
                });
            }
        },
    },
};
</script>

<style scoped lang="scss">
.kpi-panel {
    height: 100%;
    border-top: 4px solid #94a3b8;
}
.kpi-receivable { border-top-color: #22c55e; }
.kpi-payable   { border-top-color: #ef4444; }
.kpi-net       { border-top-color: #3b82f6; }

.kpi-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.5rem;

    i { margin-right: 0.4rem; }
}
.kpi-total {
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.1;
    color: #111827;

    &.ok  { color: #16a34a; }
    &.bad { color: #dc2626; }
}
.kpi-count {
    font-size: 0.85rem;
    color: var(--text-color-secondary);
    margin-top: 0.15rem;
}
.kpi-breakdown {
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
}
.kpi-chip {
    flex: 1;
    background: rgba(148, 163, 184, 0.12);
    border-radius: 8px;
    padding: 0.5rem 0.6rem;
    display: flex;
    flex-direction: column;
}
.kpi-chip-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--text-color-secondary);
}
.kpi-chip-value {
    font-size: 1.05rem;
    font-weight: 700;
    &.ok  { color: #16a34a; }
    &.bad { color: #dc2626; }
}
.kpi-net-hint {
    margin-top: 1rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-color-secondary);
}

.table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1rem;
}
.table-filters {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.row-actions {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;

    .p-button { flex: 0 0 auto; }
    :deep(.p-button-label) { white-space: nowrap; }
}

.abono-link {
    cursor: pointer;
    color: #2563eb;
    font-weight: 600;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;

    &:hover { text-decoration: underline; }
}

@media screen and (max-width: 640px) {
    .table-toolbar { flex-direction: column; align-items: stretch; }
    .table-filters { justify-content: space-between; }
}
</style>
